import { useCallback, useEffect, useState, useMemo } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';

import MatterportShowcase from 'components/organisms/MatterportShowcase';

import type { TourData } from 'utils/types/customization';

import exhibitionConfig, { imageAlignConfig } from 'exhibition.config';

const tourPathKeyMap: { [path: string]: string } = {};

exhibitionConfig.displayTourList?.forEach((key) => {
    const { path } = exhibitionConfig.tourData[key];

    if (path) {
        tourPathKeyMap[path] = key;
    }
});

const useQuery = () => {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
};

const Home: React.FC = () => {
    const history = useHistory();
    const { tour: tourPath } = useParams<{ tour: string }>();
    const query = useQuery();

    const [initPos] = useState({
        s: query.get('s'),
        x: query.get('x'),
        y: query.get('y'),
    });

    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);

    const onClickTour = useCallback(({ path }: TourData) => {
        history.push(`/${path}`);
    }, []);

    useEffect(() => {
        const resizeHandler = () => {
            setWidth(window.innerWidth);
            setHeight(window.innerHeight);
        };

        resizeHandler();

        window.addEventListener('resize', resizeHandler);
        window.addEventListener('orientationchange', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
            window.removeEventListener('orientationchange', resizeHandler);
        };
    }, []);

    useEffect(() => {
        const title = tourPath
            ? exhibitionConfig.tourData[tourPathKeyMap[tourPath]].title
            : exhibitionConfig.defaultTitle;

        document.title = title;
    }, [tourPath]);

    return (
        <div style={{ width, height }}>
            <MatterportShowcase
                // init value
                initTour={tourPath ? tourPathKeyMap[tourPath] : exhibitionConfig.defaultTour}
                initPos={
                    initPos.s && initPos.x && initPos.y
                        ? {
                              s: initPos.s,
                              x: parseFloat(initPos.x),
                              y: parseFloat(initPos.y),
                          }
                        : undefined
                }
                // -------------------

                // tools
                info={exhibitionConfig.tools.info}
                infoDefault={exhibitionConfig.tools.infoDefault}
                position={exhibitionConfig.tools.position}
                positionDefault={exhibitionConfig.tools.positionDefault}
                highlight={exhibitionConfig.tools.highlight}
                tour={exhibitionConfig.tools.tour}
                view={exhibitionConfig.tools.view}
                floors={exhibitionConfig.tools.floors}
                share={exhibitionConfig.tools.share}
                screenshot={
                    process.env.NODE_ENV === 'development'
                        ? true
                        : exhibitionConfig.tools.screenshot
                }
                getLocation={
                    process.env.NODE_ENV === 'development'
                        ? true
                        : exhibitionConfig.tools.getLocation
                }
                // -------------------

                logo={exhibitionConfig.logo}
                // menu
                menu={exhibitionConfig.menu}
                intro={exhibitionConfig.intro}
                tourList={exhibitionConfig.displayTourList}
                tourData={exhibitionConfig.tourData}
                fixedLink={exhibitionConfig.fixedLink}
                // -------------------

                // music
                musicSrc={exhibitionConfig.music}
                // -------------------

                // images config
                imageAlignDefault={imageAlignConfig.default}
                imageAlignExceptions={imageAlignConfig.exceptions}
                // -------------------

                // actions
                onClickTour={onClickTour}
                // -------------------
            />
        </div>
    );
};

export default Home;
